import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useExclusiveCollectionGate } from 'hooks/use-exclusive-collection-gate';

import { MobileOnly, DesktopOnly } from 'shared/components';
import Menu from 'components/menu';
import { MainDispensaryLayout } from 'components/layouts/main-dispensary-layout';
import { CategorySubcategoryPills } from 'components/category-subcategory-pills';
import { CollectionsHeader } from 'src/dispensary/collections/collections-header';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';
import { useCollections } from 'src/dispensary/collections/use-collections';
import { Typography } from 'src/typography';
import { CategorySubcategories } from 'src/components/menu-sidebar/category-subcategory-filter/types';

export function DispensaryCollectionsPage(): JSX.Element | null {
  const { dispensary } = useDispensary();
  const router = useRouter();
  const { menuType } = useCart();

  const flags = useFlags();
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;

  const slug = router.query.slug as string;
  const page = Number(router.query.page ?? 1);
  const limit = 30;
  const offset = (page - 1) * limit;

  const { collection, collectionDisplayMode, loading, error } = useCollections({
    collectionId: slug,
    dispensaryId: dispensary?.id,
    menuType,
    limit,
    offset,
  });

  const { categoriesSubcategories } = useMenuFilterOptions({ collectionId: collection?.id });

  const {
    cardBadgeColor,
    customCategory,
    description,
    image,
    isAccessibleToCurrentUser,
    isExclusive,
    productCount,
    products,
    title,
  } = collection ?? {};

  const isForbiddenError = (error?.graphQLErrors ?? []).some((err: any) => err.extensions?.code === 'FORBIDDEN');

  useExclusiveCollectionGate({
    isExclusive: isForbiddenError ? true : isExclusive,
    isAccessibleToCurrentUser: isForbiddenError ? false : isAccessibleToCurrentUser,
    loading,
  });

  if (!dispensary) {
    return null;
  }

  // In case the flag is disabled after an exclusive collection is created.
  // We can remove this after the flag is GA.
  if (!isExclusiveCollectionsEnabled && collectionDisplayMode === 'hidden') {
    return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
  }

  // Don't show error page for FORBIDDEN errors - let the gate modal handle it
  if (!loading && !isForbiddenError && (error || !collection)) {
    return <WhoopsContent reason={WhoopsReason.serverError} />;
  }

  // While gate modal is handling FORBIDDEN error, show loading state
  if (isForbiddenError) {
    return null;
  }

  const collectionsHeader = (
    <CollectionsHeader
      title={title ?? ''}
      description={description}
      image={image}
      customCategory={customCategory}
      isExclusive={isExclusive}
      cardBadgeColor={cardBadgeColor}
    />
  );

  return (
    <>
      <MenuWrapper>
        <MobileOnly>
          <CollectionsMobileHeaderWrapper>{collectionsHeader}</CollectionsMobileHeaderWrapper>

          <MobileHeadingContainer>
            <CollectionsHeadingContainer>
              <Typography.Heading size='medium' tag='h1'>
                {customCategory ? title : 'All Products'}
              </Typography.Heading>
            </CollectionsHeadingContainer>

            {!customCategory && (
              <CategorySubcategoryPills categoriesSubcategories={categoriesSubcategories as CategorySubcategories[]} />
            )}
          </MobileHeadingContainer>
        </MobileOnly>
      </MenuWrapper>

      <Menu
        totalCount={productCount}
        totalPages={Math.ceil((productCount ?? 0) / limit)}
        products={products}
        loading={loading}
        dispensary={dispensary}
        lastCrumbLabel={title}
        heading={customCategory ? title : 'All Products'}
        description={description}
        customHeaderComponent={<StyledDesktopOnly>{collectionsHeader}</StyledDesktopOnly>}
        page='collection'
        collectionId={collection?.id}
        customCategory={collection?.customCategory}
      />
    </>
  );
}

DispensaryCollectionsPage.layout = MainDispensaryLayout;

const MenuWrapper = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.customized.maxSiteWidth}px;
  width: 100%;
`;

const CollectionsMobileHeaderWrapper = styled.div`
  padding: 20px 16px 0;
`;

const CollectionsHeadingContainer = styled.div`
  padding: 0 0 0 16px;
`;

const MobileHeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: scroll;
  margin-bottom: -9px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const StyledDesktopOnly = styled(DesktopOnly)`
  width: 100%;
`;
